/**
 * New Business by Salesperson — Finance & Mgmt.
 * Created 2026-05-06 per Chris Georghiou request.
 * Rewritten 2026-05-08 per Pat Kross reconciliation:
 *   - Adds change orders (Sage JCM_MASTER__CHANGE_ORDER) to the picture.
 *   - Closed-jobs filter removed — Status is irrelevant; only Date matters.
 *   - YTD (calendar year) replaced with FYTD (ACS FY = Oct 1 → Sep 30).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Grid, Chip, Tabs, Tab,
  TextField, FormControl, InputLabel, Select, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, CircularProgress, IconButton, Tooltip, ButtonGroup,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon, Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getNewBusiness, exportNewBusinessXlsx } from '../services/api';
import { useSortableTable } from '../utils/useSortableTable';

const fmt$ = (n) => (n === null || n === undefined || n === '') ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n));
const fmtMonth = (ym) => {
  if (!ym) return '—';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};
const fmtDate = (d) => !d ? '—' : new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });

const SummaryCard = ({ label, value, sub }) => (
  <Paper elevation={2} sx={{ p: 1.5 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight="bold">{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

// ─── Date helpers ───────────────────────────────────────────────────────────
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, d.getDate()); }
// ACS FY = Oct 1 → Sep 30. Returns a Date for the start of the FY containing `d`.
function fyStart(d) {
  return d.getMonth() >= 9
    ? new Date(d.getFullYear(), 9, 1)
    : new Date(d.getFullYear() - 1, 9, 1);
}

export default function NewBusiness() {
  const navigate = useNavigate();
  const today = new Date();

  const [from, setFrom]               = useState(ymd(addMonths(startOfMonth(today), -11)));
  const [to, setTo]                   = useState(ymd(today));
  const [includeCOs, setIncludeCOs]   = useState(true);
  const [salesman, setSalesman]       = useState('');
  const [tab, setTab]                 = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [data, setData]       = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getNewBusiness({
        from, to,
        include_change_orders: String(includeCOs),
        ...(salesman ? { salesman } : {}),
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applyPreset = (preset) => {
    const t = new Date();
    if (preset === 'mtd')   { setFrom(ymd(startOfMonth(t)));               setTo(ymd(t)); }
    if (preset === 'last')  {
      const last = addMonths(startOfMonth(t), -1);
      const lastEnd = new Date(t.getFullYear(), t.getMonth(), 0);
      setFrom(ymd(last)); setTo(ymd(lastEnd));
    }
    if (preset === 'qtd')   {
      const qStart = new Date(t.getFullYear(), Math.floor(t.getMonth()/3)*3, 1);
      setFrom(ymd(qStart)); setTo(ymd(t));
    }
    if (preset === 'fytd')  { setFrom(ymd(fyStart(t)));                    setTo(ymd(t)); }
    if (preset === 't12')   { setFrom(ymd(addMonths(startOfMonth(t), -11))); setTo(ymd(t)); }
  };

  // Tables (house rule: sortable)
  const smTable    = useSortableTable(data?.by_salesman || [], { defaultKey: 'total',      defaultDir: 'desc' });
  const monthTable = useSortableTable(data?.by_month    || [], { defaultKey: 'month',      defaultDir: 'asc'  });
  const jobsTable  = useSortableTable(data?.rows        || [], { defaultKey: 'basis_date', defaultDir: 'desc' });

  // Heatmap matrix (salesman × month)
  const heatmap = useMemo(() => {
    if (!data?.by_salesman_month) return null;
    const max = Math.max(0, ...data.by_salesman_month.flatMap(r => r.months.map(m => m.total)));
    return { rows: data.by_salesman_month, months: data.months || [], max };
  }, [data]);

  // 2026-05-18 (Chris Georghiou): the old export was a flat CSV with no
  // salesperson grouping. Chris needs an Excel workbook that matches what
  // he sees on screen — By Salesman summary first, then items grouped
  // under each salesperson with subtotals. The server now produces this as
  // a real .xlsx with five sheets (Summary, By Salesman, By Salesman
  // Detail, By Month, All Items).
  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    if (!data?.rows?.length) return;
    setExporting(true);
    try {
      const res = await exportNewBusinessXlsx({
        from, to,
        include_change_orders: String(includeCOs),
        ...(salesman ? { salesman } : {}),
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `New_Business_by_Salesperson_${from}_to_${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TrendingUpIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">New Business by Salesperson</Typography>
          {data?.totals && (
            <Chip label={`${data.totals.item_count} items`} size="small" variant="outlined" />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={exportXlsx}
            disabled={!data?.rows?.length || exporting}
          >
            {exporting ? 'Building Excel…' : 'Export Excel'}
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Matches Sage New Business Report. Counts (a) <i>Original_Contract_Amount</i> for jobs whose Sage Start Date falls in the window, plus (b) <i>Approved_Price</i> for change orders dated in the window. Closed jobs included. Date basis: Sage Revised/Estimated Start Date for new jobs, Sage CO Date for change orders. Fiscal Year: Oct 1 → Sep 30.
      </Typography>

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 1.5, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md="auto">
            <ButtonGroup size="small">
              <Button onClick={() => applyPreset('mtd')}>MTD</Button>
              <Button onClick={() => applyPreset('last')}>Last Month</Button>
              <Button onClick={() => applyPreset('qtd')}>QTD</Button>
              <Button onClick={() => applyPreset('fytd')}>FYTD</Button>
              <Button onClick={() => applyPreset('t12')}>T12</Button>
            </ButtonGroup>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField size="small" type="date" fullWidth label="From" InputLabelProps={{ shrink: true }} value={from} onChange={e => setFrom(e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField size="small" type="date" fullWidth label="To" InputLabelProps={{ shrink: true }} value={to} onChange={e => setTo(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Change Orders</InputLabel>
              <Select label="Change Orders" value={includeCOs ? 'yes' : 'no'} onChange={e => setIncludeCOs(e.target.value === 'yes')}>
                <MenuItem value="yes">Include COs</MenuItem>
                <MenuItem value="no">New jobs only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField size="small" fullWidth label="Salesman filter" value={salesman} onChange={e => setSalesman(e.target.value)} />
          </Grid>
          <Grid item xs={12} md="auto">
            <Button variant="contained" onClick={load}>Apply</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary */}
      {data?.totals && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <SummaryCard label="Total New Business" value={fmt$(data.totals.total)} sub={`${from} → ${to}`} />
          </Grid>
          <Grid item xs={6} md={3}>
            <SummaryCard label="New Jobs" value={`${data.totals.new_job_count}`} sub={fmt$(data.totals.new_jobs_total)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <SummaryCard label="Change Orders" value={`${data.totals.co_count}`} sub={fmt$(data.totals.co_total)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <SummaryCard label="Top Salesperson" value={data.by_salesman?.[0]?.key || '—'} sub={fmt$(data.by_salesman?.[0]?.total)} />
          </Grid>
        </Grid>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <Paper elevation={1}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
            <Tab label={`By Salesman (${data.by_salesman?.length || 0})`} />
            <Tab label="Salesman × Month" />
            <Tab label={`By Month (${data.by_month?.length || 0})`} />
            <Tab label={`Items (${data.rows?.length || 0})`} />
          </Tabs>

          {tab === 0 && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {smTable.sortableCell('key',             'Salesperson')}
                    {smTable.sortableCell('new_jobs',        'New Jobs', 'right')}
                    {smTable.sortableCell('new_jobs_total',  'New Jobs $', 'right')}
                    {smTable.sortableCell('change_orders',   'COs', 'right')}
                    {smTable.sortableCell('co_total',        'CO $', 'right')}
                    {smTable.sortableCell('client_count',    'Clients', 'right')}
                    {smTable.sortableCell('total',           'Total $', 'right')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {smTable.sortedRows.map(g => (
                    <TableRow key={g.key} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{g.key}</TableCell>
                      <TableCell align="right">{g.new_jobs}</TableCell>
                      <TableCell align="right">{fmt$(g.new_jobs_total)}</TableCell>
                      <TableCell align="right">{g.change_orders}</TableCell>
                      <TableCell align="right">{fmt$(g.co_total)}</TableCell>
                      <TableCell align="right">{g.client_count}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt$(g.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 1 && heatmap && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 3 }}>Salesperson</TableCell>
                    {heatmap.months.map(m => (
                      <TableCell key={m} align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtMonth(m)}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {heatmap.rows.map(row => (
                    <TableRow key={row.salesman} hover>
                      <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: 'background.paper' }}>{row.salesman}</TableCell>
                      {row.months.map(m => {
                        const intensity = heatmap.max > 0 ? Math.min(1, m.total / heatmap.max) : 0;
                        const bg = m.total > 0 ? `rgba(46, 125, 50, ${0.08 + intensity * 0.5})` : 'transparent';
                        return (
                          <TableCell key={m.month} align="right" sx={{ bgcolor: bg, whiteSpace: 'nowrap' }}>
                            {m.total > 0 ? fmt$(m.total) : '—'}
                          </TableCell>
                        );
                      })}
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt$(row.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 2 && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {monthTable.sortableCell('month',          'Month')}
                    {monthTable.sortableCell('items',          'Items', 'right')}
                    {monthTable.sortableCell('new_jobs_total', 'New Jobs $', 'right')}
                    {monthTable.sortableCell('co_total',       'CO $', 'right')}
                    {monthTable.sortableCell('total',          'Total $', 'right')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthTable.sortedRows.map(m => (
                    <TableRow key={m.month} hover>
                      <TableCell>{fmtMonth(m.month)}</TableCell>
                      <TableCell align="right">{m.items}</TableCell>
                      <TableCell align="right">{fmt$(m.new_jobs_total)}</TableCell>
                      <TableCell align="right">{fmt$(m.co_total)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt$(m.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 3 && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {jobsTable.sortableCell('basis_date',      'Date')}
                    {jobsTable.sortableCell('kind',            'Kind')}
                    {jobsTable.sortableCell('job_number',      'Job#')}
                    {jobsTable.sortableCell('change_order',    'CO#')}
                    {jobsTable.sortableCell('name',            'Description')}
                    {jobsTable.sortableCell('client_name',     'Client')}
                    {jobsTable.sortableCell('salesman',        'Salesman')}
                    {jobsTable.sortableCell('project_manager', 'PM')}
                    {jobsTable.sortableCell('status',          'Status')}
                    {jobsTable.sortableCell('contract_amount', 'Amount', 'right')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobsTable.sortedRows.map((r, i) => (
                    <TableRow key={`${r.job_number}-${r.change_order || 'job'}-${i}`} hover>
                      <TableCell>{fmtDate(r.basis_date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.kind === 'change_order' ? 'CO' : 'New Job'}
                          size="small"
                          color={r.kind === 'change_order' ? 'warning' : 'primary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell><Chip label={r.job_number || '—'} size="small" variant="outlined" /></TableCell>
                      <TableCell>{r.change_order || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</TableCell>
                      <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.client_name || '—'}</TableCell>
                      <TableCell>{r.salesman || '—'}</TableCell>
                      <TableCell>{r.project_manager || '—'}</TableCell>
                      <TableCell><Chip label={r.status || '—'} size="small" /></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt$(r.contract_amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
}
